import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';

@Component({
    selector: 'ca-eight-by-eight-integration-settings',
    templateUrl: './eight-by-eight-integration-settings.component.html',
    styleUrls: ['./eight-by-eight-integration-settings.component.scss'],
    standalone: false
})
export class EightByEightIntegrationSettingsComponent implements OnInit {
  form: FormGroup;

  private readonly prefix = 'EightByEightIntegration';
  private readonly sourceName = `${this.prefix}.SourceName`;
  private readonly username = `${this.prefix}.Username`;
  private readonly password = `${this.prefix}.Password`;
  private readonly apiKey = `${this.prefix}.ApiKey`;
  private readonly apiSecret = `${this.prefix}.ApiSecret`;
  private readonly region = `${this.prefix}.Region`;
  private readonly timeZone = `${this.prefix}.TimeZone`;
  private readonly migrationStartDate = `${this.prefix}.MigrationStartDate`;
  private readonly scimBaseUrl = `${this.prefix}.ScimBaseUrl`;
  private readonly scimToken = `${this.prefix}.ScimToken`;

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {
    this.form = this.fb.group({
      sourceName: this.fb.group({
        name: this.sourceName,
        value: [
          this.config.getSetting(this.sourceName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      username: this.fb.group({
        name: this.username,
        value: [
          this.config.getSetting(this.username),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      password: this.fb.group({
        name: this.password,
        value: [
          this.config.getSetting(this.password),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      apiKey: this.fb.group({
        name: this.apiKey,
        value: [
          this.config.getSetting(this.apiKey),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      apiSecret: this.fb.group({
        name: this.apiSecret,
        value: [
          this.config.getSetting(this.apiSecret),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      region: this.fb.group({
        name: this.region,
        value: [
          this.config.getSetting(this.region),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      timeZone: this.fb.group({
        name: this.timeZone,
        value: [
          this.config.getSetting(this.timeZone),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      migrationStartDate: this.fb.group({
        name: this.migrationStartDate,
        value: [
          this.config.getSetting(this.migrationStartDate),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      scimBaseUrl: this.fb.group({
        name: this.scimBaseUrl,
        value: [
          this.config.getSetting(this.scimBaseUrl),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      scimToken: this.fb.group({
        name: this.scimToken,
        value: [
          this.config.getSetting(this.scimToken),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
    });
  }

  ngOnInit(): void {}

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    settings.push({
      settingName: this.form.get('sourceName').get('name').value,
      settingValue: this.form.get('sourceName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('username').get('name').value,
      settingValue: this.form.get('username').get('value').value,
    });

    if (this.form.get('password').get('value').value) {
      settings.push({
        settingName: this.form.get('password').get('name').value,
        settingValue: this.form.get('password').get('value').value,
      });
    }

    if (this.form.get('apiKey').get('value').value) {
      settings.push({
        settingName: this.form.get('apiKey').get('name').value,
        settingValue: this.form.get('apiKey').get('value').value,
      });
    }

    if (this.form.get('apiSecret').get('value').value) {
      settings.push({
        settingName: this.form.get('apiSecret').get('name').value,
        settingValue: this.form.get('apiSecret').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('region').get('name').value,
      settingValue: this.form.get('region').get('value').value,
    });

    settings.push({
      settingName: this.form.get('timeZone').get('name').value,
      settingValue: this.form.get('timeZone').get('value').value,
    });

    settings.push({
      settingName: this.form.get('migrationStartDate').get('name').value,
      settingValue: this.form.get('migrationStartDate').get('value').value,
    });

    settings.push({
      settingName: this.form.get('scimBaseUrl').get('name').value,
      settingValue: this.form.get('scimBaseUrl').get('value').value,
    });

    if (this.form.get('scimToken').get('value').value) {
      settings.push({
        settingName: this.form.get('scimToken').get('name').value,
        settingValue: this.form.get('scimToken').get('value').value,
      });
    }

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }
}
